/**
 * Magic link (email link) login view for Raven Resume.
 * Renders a centered login card with email-only sign-in via Firebase magic links,
 * plus a "Request Access" mode for new users.
 *
 * NOTE: Email Link (passwordless sign-in) must be enabled in
 * Firebase Console > Authentication > Sign-in method > Email/Password >
 * Enable "Email link (passwordless sign-in)".
 *
 * Usage:
 *   import { LoginView } from './lib/LoginView.js';
 *   const login = new LoginView({
 *       title: 'Raven Resume',
 *       subtitle: 'AI-powered resume screening',
 *       onSendLink: (email) => sendMagicLink(email),
 *       onCompleteSignIn: () => completeMagicLinkSignIn(),
 *       onRequestAccess: (email, name, reason) => requestAccess(email, name, reason),
 *   });
 *   login.hide();   // after successful auth
 *   login.show();   // on sign-out
 */
export class LoginView {
    constructor({
        title = 'Raven Resume',
        subtitle = 'AI-powered resume screening',
        onSendLink = null,
        onCompleteSignIn = null,
        onRequestAccess = null,
        topOffset = 56
    } = {}) {
        this._title = title;
        this._subtitle = subtitle;
        this._onSendLink = onSendLink;
        this._onCompleteSignIn = onCompleteSignIn;
        this._onRequestAccess = onRequestAccess;
        this._topOffset = topOffset;
        this._el = null;
        this._submitBtn = null;
        this._errorEl = null;
        this._successEl = null;
        this._isRequestMode = false;

        this._render();
        this._checkMagicLinkCallback();
    }

    /** Check if the current URL is a magic link callback. */
    _checkMagicLinkCallback() {
        if (this._onCompleteSignIn) {
            Promise.resolve(this._onCompleteSignIn()).catch(() => {});
        }
    }

    /** Show the login view. */
    show() {
        if (this._el) this._el.style.display = 'flex';
    }

    /** Hide the login view. */
    hide() {
        if (this._el) this._el.style.display = 'none';
    }

    /** Display an error message below the form. */
    showError(message) {
        if (this._errorEl) {
            this._errorEl.textContent = message;
            this._errorEl.style.display = 'block';
        }
        if (this._successEl) {
            this._successEl.style.display = 'none';
        }
        this._setLoading(false);
    }

    /** Display a success message below the form. */
    showSuccess(message) {
        if (this._successEl) {
            this._successEl.textContent = message;
            this._successEl.style.display = 'block';
        }
        if (this._errorEl) {
            this._errorEl.style.display = 'none';
        }
        this._setLoading(false);
    }

    /** Clear all messages. */
    clearError() {
        if (this._errorEl) {
            this._errorEl.textContent = '';
            this._errorEl.style.display = 'none';
        }
        if (this._successEl) {
            this._successEl.textContent = '';
            this._successEl.style.display = 'none';
        }
    }

    /** Remove the component from the DOM entirely. */
    destroy() {
        if (this._el) {
            this._el.remove();
            this._el = null;
        }
    }

    _setLoading(loading) {
        if (!this._submitBtn) return;
        this._submitBtn.disabled = loading;
        if (this._isRequestMode) {
            this._submitBtn.textContent = loading ? 'Submitting...' : 'Request Access';
        } else {
            this._submitBtn.textContent = loading ? 'Sending...' : 'Send Sign-In Link';
        }
    }

    _setMode(mode) {
        this._isRequestMode = mode === 'request';
        this.clearError();

        if (this._isRequestMode) {
            this._nameField.style.display = '';
            this._reasonField.style.display = '';
            this._submitBtn.textContent = 'Request Access';
            this._toggleLink.textContent = 'Already have access? Sign In';
            this._cardTitle.textContent = 'Request Access';
            this._cardSubtitle.textContent = 'Submit a request for an admin to review';
        } else {
            this._nameField.style.display = 'none';
            this._reasonField.style.display = 'none';
            this._submitBtn.textContent = 'Send Sign-In Link';
            this._toggleLink.textContent = 'Need access? Request it here';
            this._cardTitle.textContent = this._title;
            this._cardSubtitle.textContent = this._subtitle;
        }
    }

    _handleSubmit() {
        const email = this._emailInput.value.trim();

        if (!email) {
            this.showError('Please enter your email address.');
            return;
        }

        this.clearError();
        this._setLoading(true);

        if (this._isRequestMode) {
            const name = this._nameInput.value.trim();
            const reason = this._reasonInput.value.trim();

            if (!name) {
                this.showError('Please enter your name.');
                this._setLoading(false);
                return;
            }

            if (this._onRequestAccess) {
                Promise.resolve(this._onRequestAccess(email, name, reason))
                    .then(() => {
                        this.showSuccess('Your request has been submitted. An admin will review it.');
                    })
                    .catch(() => this._setLoading(false));
            }
        } else {
            if (this._onSendLink) {
                Promise.resolve(this._onSendLink(email))
                    .then(() => {
                        this.showSuccess('Check your email for a sign-in link.');
                    })
                    .catch(() => this._setLoading(false));
            }
        }
    }

    _render() {
        const el = document.createElement('div');
        el.id = 'nm-login-view';

        el.innerHTML = `
            <style>
                #nm-login-view {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: calc(100vh - ${this._topOffset}px);
                    z-index: 50;
                    position: relative;
                }
                .nm-login-card {
                    max-width: 420px;
                    width: 100%;
                    padding: 48px 40px;
                    background: var(--bg-tertiary);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    text-align: center;
                }
                .nm-login-title {
                    font-size: 24px;
                    font-weight: 600;
                    color: var(--text);
                    margin-bottom: 8px;
                }
                .nm-login-subtitle {
                    color: var(--text-muted);
                    font-size: 13px;
                    margin-bottom: 28px;
                    margin-top: 0;
                }
                .nm-login-field {
                    width: 100%;
                    padding: 12px 16px;
                    font-size: 14px;
                    font-family: inherit;
                    color: var(--text);
                    background: var(--bg-card, var(--bg-primary));
                    border: 1px solid var(--border);
                    border-radius: 8px;
                    margin-bottom: 12px;
                    box-sizing: border-box;
                    outline: none;
                    transition: border-color 0.2s;
                }
                .nm-login-field:focus {
                    border-color: #1a3a6b;
                    box-shadow: 0 0 0 2px rgba(26, 58, 107, 0.2);
                }
                .nm-login-field::placeholder {
                    color: var(--text-muted);
                    opacity: 0.7;
                }
                .nm-login-textarea {
                    width: 100%;
                    padding: 12px 16px;
                    font-size: 14px;
                    font-family: inherit;
                    color: var(--text);
                    background: var(--bg-card, var(--bg-primary));
                    border: 1px solid var(--border);
                    border-radius: 8px;
                    margin-bottom: 12px;
                    box-sizing: border-box;
                    outline: none;
                    transition: border-color 0.2s;
                    resize: vertical;
                    min-height: 60px;
                }
                .nm-login-textarea:focus {
                    border-color: #1a3a6b;
                    box-shadow: 0 0 0 2px rgba(26, 58, 107, 0.2);
                }
                .nm-login-textarea::placeholder {
                    color: var(--text-muted);
                    opacity: 0.7;
                }
                .nm-login-btn {
                    width: 100%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    padding: 14px 24px;
                    font-size: 15px;
                    font-weight: 500;
                    font-family: inherit;
                    color: #ffffff;
                    background: #b71c2c;
                    border: 1px solid #b71c2c;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: background 0.2s, box-shadow 0.2s;
                    margin-top: 4px;
                }
                .nm-login-btn:hover:not(:disabled) {
                    background: #9a1724;
                    box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.2));
                }
                .nm-login-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .nm-login-links {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 16px;
                }
                .nm-login-link {
                    color: #1a3a6b;
                    font-size: 13px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-family: inherit;
                    padding: 0;
                    text-decoration: none;
                }
                .nm-login-link:hover {
                    text-decoration: underline;
                }
                .nm-login-error {
                    display: none;
                    margin-top: 16px;
                    padding: 10px 16px;
                    border-radius: 8px;
                    font-size: 13px;
                    background: rgba(239, 68, 68, 0.15);
                    color: var(--error, #ef4444);
                    border: 1px solid rgba(239, 68, 68, 0.3);
                    text-align: left;
                }
                .nm-login-success {
                    display: none;
                    margin-top: 16px;
                    padding: 10px 16px;
                    border-radius: 8px;
                    font-size: 13px;
                    background: rgba(34, 197, 94, 0.15);
                    color: var(--success, #22c55e);
                    border: 1px solid rgba(34, 197, 94, 0.3);
                    text-align: left;
                }
            </style>
            <div class="nm-login-card">
                <h2 class="nm-login-title">${this._title}</h2>
                <p class="nm-login-subtitle">${this._subtitle}</p>
                <input class="nm-login-field" type="email" placeholder="Email address" autocomplete="email" />
                <input class="nm-login-field nm-login-name" type="text" placeholder="Your name" style="display:none;" />
                <textarea class="nm-login-textarea nm-login-reason" placeholder="Reason for access (optional)" style="display:none;"></textarea>
                <button class="nm-login-btn" type="button">Send Sign-In Link</button>
                <div class="nm-login-links">
                    <button class="nm-login-link nm-login-toggle" type="button">Need access? Request it here</button>
                </div>
                <div class="nm-login-error"></div>
                <div class="nm-login-success"></div>
            </div>
        `;

        this._el = el;
        this._emailInput = el.querySelector('input[type="email"]');
        this._nameInput = el.querySelector('.nm-login-name');
        this._nameField = this._nameInput;
        this._reasonInput = el.querySelector('.nm-login-reason');
        this._reasonField = this._reasonInput;
        this._submitBtn = el.querySelector('.nm-login-btn');
        this._errorEl = el.querySelector('.nm-login-error');
        this._successEl = el.querySelector('.nm-login-success');
        this._toggleLink = el.querySelector('.nm-login-toggle');
        this._cardTitle = el.querySelector('.nm-login-title');
        this._cardSubtitle = el.querySelector('.nm-login-subtitle');

        // Submit on button click
        this._submitBtn.addEventListener('click', () => this._handleSubmit());

        // Submit on Enter in email field
        this._emailInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this._handleSubmit();
        });
        this._nameInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this._handleSubmit();
        });

        // Toggle between sign-in and request-access
        this._toggleLink.addEventListener('click', () => {
            if (this._isRequestMode) {
                this._setMode('signin');
            } else {
                this._setMode('request');
            }
        });

        document.body.appendChild(el);
    }
}
